/**
 * Hybrid storage utility for Secure Device Token (sdtToken)
 * Uses both localStorage AND cookies for redundancy
 */

import { setCookie, getCookie, deleteCookie, areCookiesEnabled } from './cookieUtil';

const SDT_TOKEN_PREFIX = 'sdtToken_V2';

/**
 * Generate the storage key name for a user's sdtToken
 * @param {string} email - User email
 * @returns {string} Storage key name
 */
const getTokenKeyName = (email) => {
    return `${SDT_TOKEN_PREFIX}${email}`;
}

/**
 * Save sdtToken to BOTH localStorage and cookie for redundancy
 * @param {string} email - User email
 * @param {string} token - Secure device token
 * @returns {boolean} True if saved successfully
 */
export const saveSecureDeviceToken = (email, token) => {
    if (!email || !token) {
        console.warn('[sdtStorage] Cannot save token: missing email or token');
        return false;
    }

    const keyName = getTokenKeyName(email);
    let localStorageSuccess = false;
    let cookieSuccess = false;

    // Try localStorage first
    try {
        localStorage.setItem(keyName, token);
        localStorageSuccess = true;
        console.log('[sdtStorage] Token saved to localStorage for:', email);
    } catch (e) {
        console.error('[sdtStorage] Failed to save to localStorage:', e);
    }

    // Also save to cookie as backup (14 days = MFA grace period)
    try {
        if (areCookiesEnabled()) {
            cookieSuccess = setCookie(keyName, token, 14);
            if (cookieSuccess) {
                console.log('[sdtStorage] Token saved to cookie for:', email);
            }
        } else {
            console.warn('[sdtStorage] Cookies are disabled in browser');
        }
    } catch (e) {
        console.error('[sdtStorage] Failed to save to cookie:', e);
    }

    // Success if at least one storage method worked
    return localStorageSuccess || cookieSuccess;
}

/**
 * Get sdtToken from localStorage first, fallback to cookie if not found
 * @param {string} email - User email
 * @returns {string|null} Secure device token or null if not found
 */
export const getSecureDeviceToken = (email) => {
    if (!email) {
        console.warn('[sdtStorage] Cannot get token: missing email');
        return null;
    }

    const keyName = getTokenKeyName(email);
    let token = null;

    // Try localStorage first
    try {
        token = localStorage.getItem(keyName);
        if (token) {
            console.log('[sdtStorage] Token found in localStorage for:', email);
            
            // If found in localStorage, make sure it's also in cookie
            // This re-syncs if cookie was deleted
            if (areCookiesEnabled() && !getCookie(keyName)) {
                console.log('[sdtStorage] Re-syncing token to cookie');
                setCookie(keyName, token, 14);
            }
            
            return token;
        }
    } catch (e) {
        console.error('[sdtStorage] Failed to read from localStorage:', e);
    }

    // Fallback to cookie if localStorage is empty
    try {
        token = getCookie(keyName);
        if (token) {
            console.log('[sdtStorage] Token found in cookie (localStorage was empty) for:', email);
            
            // Restore to localStorage if it was cleared
            try {
                localStorage.setItem(keyName, token);
                console.log('[sdtStorage] Token restored to localStorage from cookie');
            } catch (e) {
                console.warn('[sdtStorage] Could not restore to localStorage:', e);
            }
            
            return token;
        }
    } catch (e) {
        console.error('[sdtStorage] Failed to read from cookie:', e);
    }

    // Not found in either storage
    console.warn('[sdtStorage] No sdtToken found for user:', email);
    return null;
}

/**
 * Remove sdtToken from BOTH localStorage and cookie
 * @param {string} email - User email
 * @returns {boolean} True if removed successfully
 */
export const removeSecureDeviceToken = (email) => {
    if (!email) {
        console.warn('[sdtStorage] Cannot remove token: missing email');
        return false;
    }

    const keyName = getTokenKeyName(email);
    let success = false;

    // Remove from localStorage
    try {
        localStorage.removeItem(keyName);
        console.log('[sdtStorage] Token removed from localStorage for:', email);
        success = true;
    } catch (e) {
        console.error('[sdtStorage] Failed to remove from localStorage:', e);
    }

    // Remove from cookie
    try {
        deleteCookie(keyName);
        console.log('[sdtStorage] Token removed from cookie for:', email);
        success = true;
    } catch (e) {
        console.error('[sdtStorage] Failed to remove from cookie:', e);
    }

    return success;
}

/**
 * Check if sdtToken exists for a user
 * @param {string} email - User email
 * @returns {boolean} True if token exists in either storage
 */
export const hasSecureDeviceToken = (email) => {
    const token = getSecureDeviceToken(email);
    return token !== null;
}
